import React, { useState, useEffect } from "react";
import {
  BaseModal,
  BaseModalBody,
  BaseModalHeader,
  BaseModalFooter,
  Label,
} from "@src/common/components";
import Input from "@src/common/components/Input/Input";
import CourieButton from "@src/common/components/Button/Button";

interface SaveViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  initialName?: string;
  isEditing?: boolean;
  isLoading?: boolean;
}

function SaveViewModal({
  isOpen,
  onClose,
  onSave,
  initialName = "",
  isEditing = false,
  isLoading = false,
}: SaveViewModalProps) {
  const [viewName, setViewName] = useState(initialName);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      setViewName(initialName);
      setError(undefined);
    }
  }, [isOpen, initialName]);

  const handleSave = () => {
    const trimmedName = viewName.trim();
    if (!trimmedName) {
      setError("View name is required");
      return;
    }
    if (trimmedName.length > 50) {
      setError("View name must be 50 characters or less");
      return;
    }
    onSave(trimmedName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleSave();
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}   
      overrides={{
        Root: {
          style: {
            zIndex: 20,
          },
        },
      }}
    >
      <BaseModalHeader>
        {isEditing ? "Edit Saved View" : "Save Current View"}
      </BaseModalHeader>
      <BaseModalBody>
        <div className="flex flex-col gap-2">
          <Label htmlFor="saved-view-name" value="View Name" />
          <Input
            id="saved-view-name"
            value={viewName}
            onChange={(e) => {
              setViewName(e.target.value);
              setError(undefined);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Enter a name for this view"
            error={!!error}
            autoFocus
          />
          {error && <span className="text-sm text-red-500">{error}</span>}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This will save your current filters and sorting configuration.
          </p>
        </div>
      </BaseModalBody>
      <BaseModalFooter>
        <div className="flex justify-end gap-2">
          <CourieButton
            color="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </CourieButton>
          <CourieButton onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : isEditing ? "Update" : "Save"}
          </CourieButton>
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
}

export default SaveViewModal;
